<template>
  <div id="listWidget" class="mb-5">
    <ul class="breadcrumb">
      <li v-if="zone" class="breadcrumb-item">{{ zone?.name }}</li>
      <li class="breadcrumb-item active">조명제어기 등록</li>
    </ul>
    <div>
      <!-- <h4 class="mb-2">조명제어기 등록</h4> -->

      <InstructionStepsCard
        title="등록 단계"
        :steps="registSteps"
      />

      <SettingWarningBox
        message="조명제어기 설정시 한 장치만 활성화 되어 있어야합니다."
      />

      <ControllerModelGuideCard />

      <!-- 연결 버튼 -->
      <div class="action-button-container mb-3">
        <button
          type="button"
          class="btn btn-primary w-100"
          @click="connectionDialog.show"
          :disabled="isRegistrationComplete"
        >
          <i class="fa-solid fa-link me-2"></i>
          {{ isRegistrationComplete ? '완료' : '연결' }}
        </button>
      </div>

    </div>

    <!-- 연결된 컨트롤러 정보 카드 -->
    <ControllerCard
      v-if="connectedController._id"
      :controller="connectedController"
      controller-type="조명제어기"
      mode="registration"
      @close="connectedController._id = ''"
    />

    <!-- ControllerConnectionDialog -->
    <ControllerConnectionDialog
      :visible="connectionDialog.visible"
      title="조명제어기 연결 및 설정"
      actionType="register"
      :needsProvisioning="true"
      :needsWifiInput="true"
      :initialWifiSsid="settingParams.ssid"
      :initialWifiPassword="settingParams.pwd"
      @complete="onConnectionComplete"
      @cancel="connectionDialog.hide"
    />

    <!-- addControllerModal -->
    <PopDialog
      title="조명제어기 추가"
      :visible="addControllerModal.visible"
      :okCallback="onRegist"
      :cancelCallback="addControllerModal.hide"
      :clickable="false"
    >
      시리얼 넘버 :
      <input
        type="text"
        class="form-control form-control-lg fs-15px"
        v-model="connectedController._id"
      />
      모델 코드 :
      <input
        type="text"
        class="form-control form-control-lg fs-15px"
        v-model="connectedController.model"
      />
      펌웨어 버전 :
      <input
        type="text"
        class="form-control form-control-lg fs-15px"
        v-model="connectedController.version"
      />
    </PopDialog>
  </div>
</template>
<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import Axios, { ZoneApi, GroupApi, CtrlApi } from '@/api';

import { useRouter, useRoute, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import CardBody from '@/common/template/components/bootstrap/CardBody.vue';

import ControllerCard from './components/ControllerCard.vue';
import ControllerConnectionDialog from './components/ControllerConnectionDialog.vue';
import { SettingConfig } from '@/common/constants';
import useMobile from '@/common/hooks/useMobile';
import useMqttControl from '@/common/hooks/useMqttControl';
import InstructionStepsCard from './components/InstructionStepsCard.vue';
import SettingWarningBox from './components/SettingWarningBox.vue';
import ControllerModelGuideCard from './components/ControllerModelGuideCard.vue';

const registSteps = [
  '조명제어기의 <strong>리셋(RESET) 버튼</strong>을 길게 눌러서 세팅 모드로 진입하시고 LED를 확인하십시오.',
  '<strong>연결 버튼</strong>을 눌러서 조명제어기에 접속하십시오.',
  '로컬 WiFi 정보를 설정해주십시오.',
  '조명제어기를 서버에 등록합니다.',
];

const mqttControl = reactive(useMqttControl());
const { emitter, mobile, logger } =
  getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();
const account = computed(() => store.getters['account']);
const isRegistrationComplete = computed(() => {
  // 등록이 완료된 경우에만 true 반환
  const _id = connectedController.value._id;
  const step2Status = registrationSteps.value[2]?.status;
  
  // 명시적인 타입 체크와 빈 문자열 검증
  const hasController = Boolean(_id && typeof _id === 'string' && _id.trim().length > 0);
  const isCompleted = step2Status === 'completed';
  const result = hasController && isCompleted;
  
  // 디버깅: 상태 확인 (항상 출력)
  console.log('isRegistrationComplete check:', {
    hasController: hasController,
    isCompleted: isCompleted,
    _id: `"${_id}"`,
    _idType: typeof _id,
    _idLength: _id?.length,
    step2Status: step2Status,
    step2Index: 2,
    allSteps: registrationSteps.value.map((s, i) => ({ index: i, title: s.title, status: s.status })),
    result: result
  });
  
  return result;
});
const connectionDialog = reactive(useModal());
const addControllerModal = reactive(useModal());
const {
  requestTransferController,
} = useMobile();
const zones = computed(() => store.getters['home/getZones']);

const forceRoute = ref(false);
const zone = ref(undefined);

// 등록 단계 상태 관리 (서버 등록만 관리)
const registrationSteps = ref([
  {
    title: '연결 및 설정',
    status: 'pending', // pending, in_progress, completed, failed
    message: '',
    error: '',
  },
  {
    title: '서버 등록',
    status: 'pending',
    message: '',
    error: '',
  },
  {
    title: '완료',
    status: 'pending',
    message: '',
    error: '',
  },
]);

const updateStepStatus = (stepIndex, status, message = '', error = '') => {
  if (stepIndex >= 0 && stepIndex < registrationSteps.value.length) {
    registrationSteps.value[stepIndex].status = status;
    registrationSteps.value[stepIndex].message = message;
    registrationSteps.value[stepIndex].error = error;
  }
};

const resetSteps = () => {
  registrationSteps.value.forEach((step) => {
    step.status = 'pending';
    step.message = '';
    step.error = '';
  });
};

const settingParams = ref({
  // ssid: 'MOTIZEN_IoT',
  // pwd: 'motizen$$$',
  ssid : '',
  pwd :'',
  firmware_version: '',
  firmware_url: '',
  uuid: '',
  device_key: '',
  mqtt_url: process.env.VUE_APP_CTRL_MQTT_DOMAIN,
  mqtt_username: '',
  mqtt_password: '',
});

const connectedController = ref({
  _id: '',
  version: '',
  model: '',
});

onBeforeMount(() => {
  // 상태 초기화 - Vue 반응성을 유지하기 위해 속성을 직접 수정
  connectedController.value._id = '';
  connectedController.value.version = '';
  connectedController.value.model = '';
  
  // resetSteps() 함수 활용
  resetSteps();
  
  emitter.emit('onChangeHeaderSetting', {
    title: '조명제어기 등록',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
  });
});
onBeforeUnmount(() => {
  // 안전장치: 전역 로딩이 남으면 라우팅이 막힐 수 있으므로 강제 정리
  emitter.emit('hideLoading', true);
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
  });
});
onMounted(() => {
  forceRoute.value = false; // 컴포넌트 마운트 시 forceRoute 초기화
  // 중복 초기화 제거 - onBeforeMount에서 이미 초기화됨
  // Vue 반응성을 유지하기 위해 속성을 직접 수정
  connectedController.value._id = '';
  connectedController.value.version = '';
  connectedController.value.model = '';
  resetSteps();
  
  requestZone();
  console.log('BleControllerRegistPage.onMounted => zone : ', zone.value);
});

onBeforeRouteUpdate((to, from) => {
  // 같은 컴포넌트가 재사용될 때 forceRoute 초기화
  forceRoute.value = false;
  return true;
});

watch(zone, () => {
  console.log('ControllerRegistPage.watch => zone : ', zone.value);
});

const setSettingModeOff = async () => {
  forceRoute.value = true;
  const payload = { isEditMode: false };
  const result = await Axios.patch(
    ZoneApi.UPDATE_ZONE.replace(':id', zone.value._id),
    payload,
  );

  if (result.resultCode === '0000') {
    mqttControl.onPublishZone(zone.value._id, payload);
    store.dispatch('home/fetchAllZones');
  }
  // Promise가 resolve(true)되면 라우터가 자동으로 전환하므로 router.go(-1) 제거
};

const navigateAfterSettingModeLeave = async (to) => {
  const target = to.fullPath || to.path;
  if (to.path === '/settings/light/controllers') {
    router.back();
  } else {
    await router.replace(target);
  }
};

onBeforeRouteLeave((to, from) => {
  // 프로그래밍 이동(등록 완료 등)은 확인 없이 허용
  if (forceRoute.value) {
    forceRoute.value = false;
    emitter.emit('hideLoading', true);
    return true;
  }

  if (!zone.value?.isEditMode) {
    emitter.emit('hideLoading', true);
    return true;
  }

  return new Promise((resolve) => {
    if (store.getters['isLoading']) {
      emitter.emit('showPopConfirm', {
        description:
          '세팅이 진행중입니다.\n절차를 중지하고 세팅모드를 종료하시겠습니까?',
        okCallback: async () => {
          await allStopProcess();
          forceRoute.value = true;
          emitter.emit('hideLoading', true);
          await navigateAfterSettingModeLeave(to);
        },
        cancelCallback: () => {},
      });
      resolve(false);
      return;
    }

    emitter.emit('showPopAlert', {
      title: '세팅 모드',
      description: '세팅모드를 종료하시겠습니까?',
      okCallback: async () => {
        forceRoute.value = true;
        await setSettingModeOff();
        emitter.emit('hideLoading', true);
        await navigateAfterSettingModeLeave(to);
      },
      cancelCallback: () => {},
    });
    resolve(false);
  });
});

const allStopProcess = async () => {
  emitter.emit('hideLoading', true);
  await setSettingModeOff();
};

const showPopAlert = (msg) => {
  emitter.emit('showPopAlert', {
    description: msg,
  });
};

// 팝업에서 연결 및 프로비저닝 완료 시 호출
const onConnectionComplete = async (result) => {
  try {
    // 연결된 컨트롤러 정보 저장
    connectedController.value = result.controller;
    
    // WiFi 설정 정보 저장 (팝업에서 입력한 값)
    if (result.wifiSsid && result.wifiPassword) {
      settingParams.value.ssid = result.wifiSsid;
      settingParams.value.pwd = result.wifiPassword;
    }
    
    // 연결 및 설정 단계 완료 표시
    updateStepStatus(0, 'completed', '연결 및 설정 완료');
    
    // 서버 등록 진행
    updateStepStatus(1, 'in_progress', '서버 등록 중...');
    emitter.emit('showLoading');
    
    await onRegist();
    
    updateStepStatus(1, 'completed', '서버 등록 완료');
    updateStepStatus(2, 'completed', '완료');
    emitter.emit('hideLoading');
    
    await store.dispatch('home/fetchAllZones');
    
    emitter.emit('showPopAlert', {
      description: '조명제어기가 등록되었습니다.',
      okCallback: async () => {
        forceRoute.value = true;
        await setSettingModeOff();
        router.back();
      },
    });
  } catch (e) {
    emitter.emit('hideLoading');
    
    // resultCode 1001 (FailAlreadyExist) 처리
    if (e?.resultCode === '1001' || e?.message?.includes('이미 등록된')) {
      const errorMessage = e?.resultMessage || e?.message || '이미 등록된 컨트롤러입니다.\n이관받기를 시도해주세요.';
      updateStepStatus(1, 'failed', '', errorMessage);
      showPopAlert(errorMessage);
    } else {
      const errorMessage = e?.resultMessage || e?.message || '서버 등록 실패';
      updateStepStatus(1, 'failed', '', errorMessage);
      showPopAlert(errorMessage);
    }
  }
};

// 컨트롤러 등록
// MQTT 서버가 컨트롤러의 version 메시지를 받으면 자동으로 tempController를 생성하므로
// 앱에서는 tempController 생성 단계가 필요 없습니다.
const onRegist = async () => {
  
  const request = () => {
    return new Promise(async (resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await Axios.post(
            ZoneApi.REGIST_ZONE_CTRLS.replace(':id', route.params.zoneId),
            connectedController.value,
          );

          if (result?.resultCode === '0000') {
            await requestZone();
          }
          resolve(result);
        } catch (e) {
          resolve(e);
        }
      }, SettingConfig.requestInterval);
    });
  };

  return new Promise(async (resolve, reject) => {
    try {
      let result = undefined;
      for (let i = 0; i < SettingConfig.requestCount; i++) {
        emitter.emit(
          'setLoadingText',
          i === 0
            ? '조명제어기 정보를 서버에 등록중입니다.'
            : `조명제어기 정보를 서버에 ${i + 1}번째 등록 시도하고 있습니다.`,
        );
        result = await request();
        if (result?.resultCode === '0000') {
          resolve();
          return;
        }
        if (result?.resultCode === '1001') {
          // FailAlreadyExist: 이미 등록된 컨트롤러인 경우
          const errorMessage = result?.resultMessage || '이미 등록된 컨트롤러입니다.\n이관받기를 시도해주세요.';
          updateStepStatus(2, 'failed', '', errorMessage);
          reject({
            ...result,
            message: errorMessage,
          });
          emitter.emit('hideLoading', true);
          return;
        }
      }

      updateStepStatus(2, 'failed', '', result?.resultMessage || '서버 등록 실패');
      reject(
        result?.resultMessage?.length > 0
          ? result.resultMessage
          : '조명제어기 등록에 실패했습니다.',
      );
      emitter.emit('hideLoading', true);
    } catch (e) {
      console.log(e);
      reject('조명제어기 추가 중 오류가 발생하였습니다.');
      emitter.emit('hideLoading', true);
    }
  });
};


const requestZone = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.get(
        ZoneApi.ZONE_INFO.replace(':id', route.params.zoneId),
      );
      if (result.resultCode === '0000') {
        zone.value = result.resultData;
        resolve(result);
      } else {
        reject(result.resultMessage);
      }
    } catch (error) {
      reject('영역 조회에 실패했습니다.');
    }
  });
};
</script>

<style scoped>
.action-button-container {
  margin-top: 0;
}

.action-button-container .btn {
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.action-button-container .btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.step-container {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step-item {
  position: relative;
  display: flex;
  flex-direction: column;
}

.step-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0;
}

.step-indicator {
  width: 50px;
  height: 50px;
  min-width: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.step-pending {
  background-color: #e9ecef;
  color: #6c757d;
  border: 2px solid #dee2e6;
}

.step-in-progress {
  background-color: #0d6efd;
  color: white;
  border: 2px solid #0d6efd;
  animation: pulse 1.5s ease-in-out infinite;
}

.step-completed {
  background-color: #198754;
  color: white;
  border: 2px solid #198754;
}

.step-failed {
  background-color: #dc3545;
  color: white;
  border: 2px solid #dc3545;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.step-label {
  flex: 1;
  padding-top: 0.5rem;
}

.step-connector {
  position: absolute;
  left: 25px;
  top: 75px;
  width: 2px;
  height: calc(100% - 50px);
  background-color: #dee2e6;
  z-index: 0;
}

.connector-active {
  background-color: #198754;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
